import {
  FeatureFlagService,
  FlagOptions,
  SetFeatureFlagSettingOptions,
} from '@m/services/FeatureFlagService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useAllFeatureFlags = () =>
  useQuery({
    queryKey: ['feature-flags', 'all'],
    queryFn: () => FeatureFlagService.getAllFeatureFlags(),
  });

// Options is optional, but if it's provided, siteId or enterpriseId should eventually not be falsy to enable the query
export const useCustomerFeatureFlag = (name: string, options?: FlagOptions) =>
  useQuery({
    queryKey: ['feature-flags', name, 'customer', options],
    queryFn: async () => FeatureFlagService.getCustomerFeatureFlag(name, options),
    enabled: isQueryEnabled(name, options),
  });

// Options is optional, but if it's provided, siteId or enterpriseId should eventually not be falsy to enable the query
export const useSpecialistFeatureFlag = (name: string, options?: FlagOptions) =>
  useQuery({
    queryKey: ['feature-flags', name, 'specialist', options],
    queryFn: async () => FeatureFlagService.getSpecialistFeatureFlag(name, options),
    enabled: isQueryEnabled(name, options),
  });

// Options is optional, but if it's provided, siteId or enterpriseId should eventually not be falsy to enable the query
export const useAdminFeatureFlag = (name: string, options?: FlagOptions) =>
  useQuery({
    queryKey: ['feature-flags', name, 'admin', options],
    // Admin endpoint return full settings object instead of a boolean.  So use customer endpoint to get enabled/disabled status.
    queryFn: async () => FeatureFlagService.getCustomerFeatureFlag(name, options),
    enabled: isQueryEnabled(name, options),
  });

// Options is optional, but if it's provided, siteId or enterpriseId should eventually not be falsy to enable the query
export const useAdminFeatureFlagSetting = (name: string, options?: FlagOptions) =>
  useQuery({
    queryKey: ['feature-flags', name, 'settings', options],
    queryFn: async () => FeatureFlagService.getAdminFeatureFlagSetting(name, options),
    enabled: isQueryEnabled(name, options),
  });

export const useSetFeatureFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, options }: { name: string; options: SetFeatureFlagSettingOptions }) =>
      FeatureFlagService.setFeatureFlagSetting(name, options),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags', name] });
    },
  });
};

export const useCreateFeatureFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, options }: { name: string; options: SetFeatureFlagSettingOptions }) =>
      FeatureFlagService.createFeatureFlag(name, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags', 'all'] });
    },
  });
};

export const useArchiveFeatureFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) => FeatureFlagService.archiveFeatureFlag(name),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flags', name] });
    },
  });
};

export const useSetFeatureFlagForSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      options,
    }: {
      name: string;
      options: { enabled: boolean; siteId: number };
    }) => {
      const { enabled, siteId } = options;
      // Get current settings
      const current = await FeatureFlagService.getAdminFeatureFlagSetting(name);
      const sites = current?.sites || [];
      let newSites;
      if (enabled) {
        if (sites.includes(siteId)) {
          // Don't add siteId if it already exists in the list
          newSites = sites;
        } else {
          newSites = [...sites, siteId];
        }
      } else {
        // Remove siteId to disable
        newSites = sites.filter((id: number) => id !== siteId);
      }
      return FeatureFlagService.setFeatureFlagSetting(name, {
        enabled: newSites.length > 0,
        sites: newSites,
      });
    },
    onSuccess: (_, { name, options: { siteId, enabled } }) => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags', name], exact: true });
      // FeatureFlagService.getCustomerFeatureFlag doesn't update right away, so update the cache and don't invalidate the query
      queryClient.setQueryData(['feature-flags', name, 'admin', { siteId }], enabled);
    },
  });
};

function isQueryEnabled(name: string, options?: FlagOptions) {
  return (
    !!name &&
    (!options ||
      (options.siteId !== undefined && options.siteId !== null) ||
      (options.siteUuid !== undefined && options.siteUuid !== null) ||
      (options.enterpriseId !== null && options.enterpriseId !== undefined))
  );
}
